import { useImperativeHandle, type ForwardedRef, type RefObject } from 'react'
import { type positionValues, type Scrollbars } from 'react-custom-scrollbars-2'

export type IScrollbarsPropsRef = {
  scrollToTop: Scrollbars['scrollToTop']
  scrollToLeft: Scrollbars['scrollToLeft']
  scrollToRight: Scrollbars['scrollToRight']
  scrollToBottom: Scrollbars['scrollToBottom']
  scrollTop: Scrollbars['scrollTop']
  scrollLeft: Scrollbars['scrollLeft']
  getScrollHeight: Scrollbars['getScrollHeight']
  getClientHeight: Scrollbars['getClientHeight']
  getScrollTop: Scrollbars['getScrollTop']
  getValues: Scrollbars['getValues']
  container?: Scrollbars['container']
  scrollTo(options?: ScrollToOptions): void
  scrollTo(x: number, y: number): void
}

export const useScrollbarApiRef = (
  ref: ForwardedRef<IScrollbarsPropsRef>,
  scrollbarRef: RefObject<Scrollbars>
) => {
  useImperativeHandle(
    ref,
    () => {
      return {
        container: scrollbarRef.current?.container,
        getValues(): positionValues {
          if (!scrollbarRef.current) {
            return {
              clientHeight: 0,
              clientWidth: 0,
              left: 0,
              scrollHeight: 0,
              scrollLeft: 0,
              scrollTop: 0,
              scrollWidth: 0,
              top: 0,
            }
          }

          return scrollbarRef.current.getValues()
        },
        getScrollTop() {
          return scrollbarRef.current?.getScrollTop() || 0
        },
        getClientHeight() {
          return scrollbarRef.current?.getClientHeight() || 0
        },
        getScrollHeight() {
          return scrollbarRef.current?.getScrollHeight() || 0
        },
        scrollTo(optionsOrX?: ScrollToOptions | number, y?: number) {
          if (!scrollbarRef.current) return

          const container = scrollbarRef.current.container
          const view_container = container.querySelector('[class*=scrollbarView]')

          if (view_container) {
            if (typeof optionsOrX === 'number' && typeof y === 'number') {
              view_container.scrollTo(optionsOrX, y)
            }

            if (typeof optionsOrX === 'object') {
              view_container.scrollTo(optionsOrX)
            }
          }
        },
        scrollToTop() {
          scrollbarRef.current?.scrollToTop()
        },
        scrollTop(value: number) {
          scrollbarRef.current?.scrollTop(value)
        },
        scrollLeft(value: number) {
          scrollbarRef.current?.scrollLeft(value)
        },
        scrollToLeft() {
          scrollbarRef.current?.scrollToLeft()
        },
        scrollToRight() {
          scrollbarRef.current?.scrollToRight()
        },
        scrollToBottom() {
          scrollbarRef.current?.scrollToBottom()
        },
      }
    },
    [scrollbarRef]
  )
}
